import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api.config';
import '../../components/common/LoginForm.css';

/**
 * Passwort-zurücksetzen-Seite (Befund B5, 18.08.2026)
 * Wird über den Link aus der Reset-E-Mail geöffnet (?token=...).
 * Passwortregeln entsprechen dem Backend (PasswordService.ValidatePasswordDetailed):
 * mindestens 8 Zeichen, Groß- und Kleinbuchstaben sowie eine Zahl.
 */
const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const validate = (): string[] => {
    const result: string[] = [];
    if (!token) result.push('Der Link ist unvollständig. Bitte verwenden Sie den Link aus der E-Mail.');
    if (newPassword.length < 8) result.push('Das Passwort muss mindestens 8 Zeichen lang sein');
    if (!/[A-Z]/.test(newPassword)) result.push('Das Passwort muss mindestens einen Großbuchstaben enthalten');
    if (!/[a-z]/.test(newPassword)) result.push('Das Passwort muss mindestens einen Kleinbuchstaben enthalten');
    if (!/[0-9]/.test(newPassword)) result.push('Das Passwort muss mindestens eine Zahl enthalten');
    if (newPassword !== confirmPassword) result.push('Die Passwörter stimmen nicht überein');
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword, confirmPassword }),
      });

      if (response.ok) {
        setDone(true);
      } else {
        const data = await response.json().catch(() => null);
        setErrors([
          data?.message ??
            'Der Link ist ungültig, abgelaufen oder wurde bereits verwendet. Bitte fordern Sie einen neuen Link an.',
        ]);
      }
    } catch {
      setErrors(['Die Anfrage konnte nicht verarbeitet werden. Bitte versuchen Sie es später erneut.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-form-container">
      <div className="login-form-card">
        <div className="login-logo-container">
          <img src="/logo-frankenfloss.svg" alt="Frankenfloss Logo" className="login-logo" />
        </div>

        <div className="login-form-header">
          <h2>Neues Passwort vergeben</h2>
          <p>Bitte wählen Sie ein sicheres Passwort</p>
        </div>

        {done ? (
          <div className="login-form">
            <p>Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt mit dem neuen Passwort anmelden.</p>
            <button type="button" className="login-button" onClick={() => navigate('/login')}>
              Zur Anmeldung
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {errors.length > 0 && (
              <div className="error-messages">
                {errors.map((err, i) => (
                  <div key={i} className="error-message"><span>{err}</span></div>
                ))}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="newPassword">Neues Passwort</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setErrors([]); }}
                placeholder="Mindestens 8 Zeichen, Groß-/Kleinbuchstaben, Zahl"
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Neues Passwort bestätigen</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setErrors([]); }}
                placeholder="Passwort wiederholen"
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="login-button" disabled={isSubmitting}>
              {isSubmitting ? 'Wird gespeichert...' : 'Passwort ändern'}
            </button>
          </form>
        )}

        <div className="login-form-footer">
          <p>
            <button type="button" className="link-button" onClick={() => navigate('/passwort-vergessen')}>
              Neuen Link anfordern
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
